import type { IControlType, IJodit } from 'jodit/esm/types';

export const ZOOM_LEVELS = [50, 75, 90, 100, 125, 150, 200];
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 96;

const currentFontSize = (editor: IJodit): number => {
  const node = editor.s.current();
  const element = node && (node.nodeType === Node.ELEMENT_NODE ? (node as HTMLElement) : node.parentElement);
  const size = parseFloat(editor.ew.getComputedStyle(element ?? editor.editor).fontSize);
  return Number.isNaN(size) ? 16 : Math.round(size);
};

const applyFontSize = (editor: IJodit, size: number) => {
  const clamped = Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, size));
  editor.s.commitStyle({ attributes: { style: { fontSize: `${clamped}px` } } });
  editor.e.fire('rich-text-font-size');
};

export const fontSizeStepper: IControlType<IJodit> = {
  name: 'fontSizeStepper',
  tooltip: 'Font size',
  getContent: (editor) => {
    const stepper = editor.c.div('rich-text-font-size');
    const minus = editor.c.element('button', { type: 'button', 'aria-label': 'Decrease font size' }, '−');
    const value = editor.c.element('input', {
      type: 'text',
      inputmode: 'numeric',
      'aria-label': 'Font size'
    }) as HTMLInputElement;
    const plus = editor.c.element('button', { type: 'button', 'aria-label': 'Increase font size' }, '+');
    stepper.append(minus, value, plus);

    const refresh = () => {
      if (editor.ownerDocument.activeElement !== value) value.value = String(currentFontSize(editor));
    };

    minus.addEventListener('mousedown', (event) => event.preventDefault());
    plus.addEventListener('mousedown', (event) => event.preventDefault());
    minus.addEventListener('click', () => applyFontSize(editor, currentFontSize(editor) - 1));
    plus.addEventListener('click', () => applyFontSize(editor, currentFontSize(editor) + 1));
    value.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const size = parseInt(value.value, 10);
      editor.s.restore();
      if (!Number.isNaN(size)) applyFontSize(editor, size);
      editor.s.focus();
    });
    value.addEventListener('focus', () => editor.s.save());

    editor.e.on('changeSelection afterInit rich-text-font-size', refresh);
    refresh();
    return stepper;
  }
};

export const zoom: IControlType<IJodit> = {
  name: 'zoom',
  tooltip: 'Zoom',
  list: ZOOM_LEVELS,
  text: '100%',
  childTemplate: (_editor, _key, value) => `${value}%`,
  update: (editor, { state }) => {
    Object.assign(state, { text: `${editor.editor.dataset.zoom ?? 100}%` });
  },
  exec: (editor, _current, { control }) => {
    const level = Number(control.args?.[0]);
    if (!level) return false;
    const { dataset, style } = editor.editor;
    dataset.zoom = String(level);
    style.setProperty('zoom', String(level / 100));
    editor.e.fire('updateToolbar');
    return true;
  }
};

const showValueAsText =
  (control: IControlType<IJodit>, label: (editor: IJodit, value: string) => string): IControlType<IJodit>['update'] =>
  (editor, button) => {
    control.update?.(editor, button);
    const value = control.value?.(editor, button) ?? '';
    Object.assign(button.state, { icon: { ...button.state.icon, name: '' }, text: label(editor, value) });
  };

export const textLabelControls = (defaults: Record<string, IControlType<IJodit>>) => {
  const paragraphList: Record<string, string> = {
    p: 'Paragraph text',
    h1: 'Heading 1',
    h2: 'Heading 2',
    h3: 'Heading 3',
    h4: 'Heading 4',
    blockquote: 'Quote'
  };
  const fontList = defaults.font.list as Record<string, string>;
  const normalizeFont = (font: string) => font.toLowerCase().replace(/['"\s]+/g, '');
  const fontLabel = (font: string) =>
    Object.entries(fontList).find(([key]) => key && normalizeFont(key) === normalizeFont(font))?.[1] ?? 'Default';

  return {
    paragraph: {
      ...defaults.paragraph,
      list: paragraphList,
      update: showValueAsText(defaults.paragraph, (_editor, value) => paragraphList[value] ?? paragraphList.p)
    },
    font: {
      ...defaults.font,
      update: showValueAsText(defaults.font, (_editor, value) => (value ? fontLabel(value) : 'Default'))
    }
  };
};
